import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Form,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
} from "antd";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import {
  ListAiOnboardedLearners,
  UpdateAiOnboardedLearner,
} from "../../redux/actions/AiOnboardAction";

const { TabPane } = Tabs;

const STATUS_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];

const BONUS_FILTERS = [
  {
    label: "MS365",
    value: "MS365",
    preferenceKey: "ms365Preference",
    statusKey: "ms365Status",
  },
  {
    label: "LinkedIn",
    value: "LinkedIn",
    preferenceKey: "linkedinPremiumPreference",
    statusKey: "linkedinPremiumStatus",
  },
  {
    label: "Claude",
    value: "Claude",
    preferenceKey: "claudePremiumPreference",
    statusKey: "claudePremiumStatus",
  },
  {
    label: "Gemini",
    value: "Gemini",
    preferenceKey: "geminiPremiumPreference",
    statusKey: "geminiPremiumStatus",
  },
];

const EMPTY_BONUS_FILTERS = {
  bonusName: undefined,
  bonusPreference: undefined,
  bonusStatus: undefined,
};

const getId = (item) => item?._id ?? item?.id;

const statusColor = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  if (normalized === "approved" || normalized === "activated") return "green";
  if (normalized === "pending") return "gold";
  if (normalized === "rejected") return "red";
  return "default";
};

const paymentStatusColor = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  if (["paid", "success", "completed"].includes(normalized)) return "green";
  if (["pending", "processing"].includes(normalized)) return "gold";
  if (["failed", "unpaid", "cancelled", "canceled"].includes(normalized))
    return "red";
  return "default";
};

const normalizePreference = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const uniquePreferenceOptions = (list, key) => {
  const values = new Set();
  (list ?? []).forEach((item) => {
    const value = normalizePreference(item?.[key]);
    if (value) values.add(value);
  });
  return Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ label: value, value }));
};

const getCourseSubscriptions = (record) =>
  Array.isArray(record?.allAiCourseSubscriptions)
    ? record.allAiCourseSubscriptions
    : [];

const normalizeStatus = (value) => String(value ?? "").trim().toLowerCase();

const getCourseStatusLabel = (item) => {
  const explicit =
    item?.courseStatus ??
    item?.courseActivationStatus ??
    item?.aiCourseStatus ??
    item?.courseActivatedStatus;
  if (explicit !== undefined && explicit !== null && String(explicit).trim()) {
    return String(explicit).trim();
  }
  if (item?.courseActivated === true || item?.isCourseActivated === true) {
    return "Activated";
  }
  const subscriptionStatus = getCourseSubscriptions(item)
    .map(
      (course) =>
        course?.status ?? course?.courseStatus ?? course?.activationStatus
    )
    .find((status) => status !== undefined && status !== null && String(status).trim());
  if (subscriptionStatus) return String(subscriptionStatus).trim();
  const count =
    item?.aiCourseSubscriptionCount ?? getCourseSubscriptions(item).length;
  if (Number(count) > 0) return "Activated";
  return "";
};

const matchesBonusFilters = (item, filters) => {
  const bonusName = filters?.bonusName;
  const bonusPreference = filters?.bonusPreference;
  const bonusStatus = filters?.bonusStatus;
  if (!bonusName && !bonusPreference && !bonusStatus) return true;

  const targets = BONUS_FILTERS.filter(
    (bonus) => !bonusName || bonus.value === bonusName
  );

  return targets.some((bonus) => {
    if (
      bonusPreference &&
      normalizePreference(item?.[bonus.preferenceKey]) !== bonusPreference
    ) {
      return false;
    }
    if (
      bonusStatus &&
      normalizeStatus(item?.[bonus.statusKey]) !== normalizeStatus(bonusStatus)
    ) {
      return false;
    }
    return true;
  });
};

const AiOnboardedLearners = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("list");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [learners, setLearners] = useState([]);
  const [editingLearner, setEditingLearner] = useState(null);
  const [draftFilters, setDraftFilters] = useState(EMPTY_BONUS_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_BONUS_FILTERS);

  const fetchLearners = useCallback(() => {
    setLoading(true);
    dispatch(
      ListAiOnboardedLearners(
        (list) => {
          setLearners(Array.isArray(list) ? list : []);
          setLoading(false);
        },
        (errMsg) => {
          setLoading(false);
          Modal.error({
            title: "Failed to fetch learners",
            content: errMsg,
          });
        }
      )
    );
  }, [dispatch]);

  useEffect(() => {
    fetchLearners();
  }, [fetchLearners]);

  const startEdit = (record) => {
    setEditingLearner(record);
    form.setFieldsValue({
      ms365Status: record?.ms365Status ?? "Pending",
      linkedinPremiumStatus: record?.linkedinPremiumStatus ?? "Pending",
      claudePremiumStatus: record?.claudePremiumStatus ?? "Pending",
      geminiPremiumStatus: record?.geminiPremiumStatus ?? "Pending",
    });
    setActiveTab("edit");
  };

  const onSelectLearner = (id) => {
    const record = learners.find((item) => String(getId(item)) === String(id));
    if (record) startEdit(record);
  };

  const onFinish = (values) => {
    const id = getId(editingLearner);
    if (!id) {
      Modal.warning({
        title: "Select a learner",
        content: "Choose a learner from the list before saving.",
      });
      return;
    }

    setSubmitting(true);
    dispatch(
      UpdateAiOnboardedLearner(
        id,
        {
          ms365Status: values?.ms365Status,
          linkedinPremiumStatus: values?.linkedinPremiumStatus,
          claudePremiumStatus: values?.claudePremiumStatus,
          geminiPremiumStatus: values?.geminiPremiumStatus,
        },
        (response) => {
          const updated =
            response?.data && !Array.isArray(response.data)
              ? response.data
              : { ...editingLearner, ...values };
          setEditingLearner(updated);
          form.setFieldsValue({
            ms365Status: updated?.ms365Status ?? values?.ms365Status,
            linkedinPremiumStatus:
              updated?.linkedinPremiumStatus ?? values?.linkedinPremiumStatus,
            claudePremiumStatus:
              updated?.claudePremiumStatus ?? values?.claudePremiumStatus,
            geminiPremiumStatus:
              updated?.geminiPremiumStatus ?? values?.geminiPremiumStatus,
          });
          fetchLearners();
          setSubmitting(false);
          Modal.success({ title: "Learner updated successfully" });
        },
        (errMsg) => {
          setSubmitting(false);
          Modal.error({
            title: "Update failed",
            content: errMsg,
          });
        }
      )
    );
  };

  const bonusNameOptions = BONUS_FILTERS.map(({ label, value }) => ({
    label,
    value,
  }));

  const bonusPreferenceOptions = useMemo(() => {
    const selected = BONUS_FILTERS.find(
      (bonus) => bonus.value === draftFilters.bonusName
    );
    const keys = selected
      ? [selected.preferenceKey]
      : BONUS_FILTERS.map((bonus) => bonus.preferenceKey);
    const values = new Set();
    keys.forEach((key) => {
      uniquePreferenceOptions(learners, key).forEach((option) => {
        values.add(option.value);
      });
    });
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }));
  }, [learners, draftFilters.bonusName]);

  const bonusStatusOptions = useMemo(() => {
    const selected = BONUS_FILTERS.find(
      (bonus) => bonus.value === draftFilters.bonusName
    );
    const keys = selected
      ? [selected.statusKey]
      : BONUS_FILTERS.map((bonus) => bonus.statusKey);
    const values = new Set(STATUS_OPTIONS.map((option) => option.value));
    (learners ?? []).forEach((item) => {
      keys.forEach((key) => {
        const value = String(item?.[key] ?? "").trim();
        if (value) values.add(value);
      });
    });
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }));
  }, [learners, draftFilters.bonusName]);

  const applyFilters = () => setAppliedFilters({ ...draftFilters });

  const resetFilters = () => {
    setDraftFilters(EMPTY_BONUS_FILTERS);
    setAppliedFilters(EMPTY_BONUS_FILTERS);
  };

  const tableData = useMemo(() => {
    const filtered = (learners ?? []).filter((item) =>
      matchesBonusFilters(item, appliedFilters)
    );

    return filtered.map((item, index) => ({
      key: getId(item) ?? String(index),
      slNo: index + 1,
      ...item,
      _id: getId(item),
    }));
  }, [learners, appliedFilters]);

  const columns = [
    { title: "Sl No", dataIndex: "slNo", key: "slNo", width: 70 },
    { title: "Name", dataIndex: "name", key: "name", width: 180 },
    {
      title: "Phone",
      dataIndex: "registeredPhone",
      key: "registeredPhone",
      width: 140,
    },
    { title: "Email", dataIndex: "email", key: "email", width: 220 },
    { title: "City", dataIndex: "city", key: "city", width: 120 },
    {
      title: "Profession",
      dataIndex: "profession",
      key: "profession",
      width: 180,
    },
    {
      title: "Primary Objective",
      dataIndex: "primaryObjective",
      key: "primaryObjective",
      width: 240,
    },
    {
      title: "Preferred Batch",
      dataIndex: "preferredBatch",
      key: "preferredBatch",
      width: 150,
    },
    {
      title: "MS365 Preference",
      dataIndex: "ms365Preference",
      key: "ms365Preference",
      width: 150,
      render: (value) => normalizePreference(value) || "-",
    },
    {
      title: "MS365 Status",
      dataIndex: "ms365Status",
      key: "ms365Status",
      width: 140,
      render: (status) => <Tag color={statusColor(status)}>{status || "-"}</Tag>,
    },
    {
      title: "LinkedIn Premium Preference",
      dataIndex: "linkedinPremiumPreference",
      key: "linkedinPremiumPreference",
      width: 200,
      render: (value) => normalizePreference(value) || "-",
    },
    {
      title: "LinkedIn Premium Status",
      dataIndex: "linkedinPremiumStatus",
      key: "linkedinPremiumStatus",
      width: 180,
      render: (status) => <Tag color={statusColor(status)}>{status || "-"}</Tag>,
    },
    {
      title: "Claude Premium Preference",
      dataIndex: "claudePremiumPreference",
      key: "claudePremiumPreference",
      width: 200,
      render: (value) => normalizePreference(value) || "-",
    },
    {
      title: "Claude Premium Status",
      dataIndex: "claudePremiumStatus",
      key: "claudePremiumStatus",
      width: 180,
      render: (status) => <Tag color={statusColor(status)}>{status || "-"}</Tag>,
    },
    {
      title: "Gemini Premium Preference",
      dataIndex: "geminiPremiumPreference",
      key: "geminiPremiumPreference",
      width: 200,
      render: (value) => normalizePreference(value) || "-",
    },
    {
      title: "Gemini Premium Status",
      dataIndex: "geminiPremiumStatus",
      key: "geminiPremiumStatus",
      width: 180,
      render: (status) => <Tag color={statusColor(status)}>{status || "-"}</Tag>,
    },
    {
      title: "Course Status",
      key: "courseStatus",
      width: 140,
      render: (_, record) => {
        const status = getCourseStatusLabel(record);
        return <Tag color={statusColor(status)}>{status || "-"}</Tag>;
      },
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 140,
      render: (status) => (
        <Tag color={paymentStatusColor(status)}>{status || "-"}</Tag>
      ),
    },
    {
      title: "AI Course Count",
      dataIndex: "aiCourseSubscriptionCount",
      key: "aiCourseSubscriptionCount",
      width: 140,
      render: (count, record) =>
        count ?? getCourseSubscriptions(record).length ?? 0,
    },
    {
      title: "AI Course Subscriptions",
      dataIndex: "allAiCourseSubscriptions",
      key: "allAiCourseSubscriptions",
      width: 280,
      render: (subscriptions) => {
        const courses = Array.isArray(subscriptions) ? subscriptions : [];
        if (!courses.length) return "-";
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {courses.map((course, index) => (
              <Tag key={course?.courseId ?? `${course?.courseName}-${index}`}>
                {course?.courseName || course?.courseId || "-"}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => startEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  const learnerOptions = useMemo(
    () =>
      (learners ?? []).map((item) => ({
        label: `${item?.name ?? "Unnamed"} (${item?.email ?? getId(item)})`,
        value: String(getId(item)),
      })),
    [learners]
  );

  return (
    <div>
      <PageBreadcrumb route={["dashboard", "AI Onboarded Learners"]} />

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="List" key="list">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                gap: 16,
                background: "#f0f5ff",
                borderRadius: 8,
                padding: "16px 20px",
                marginBottom: 16,
              }}
            >
              <div style={{ flex: "1 1 200px", maxWidth: 280 }}>
                <div style={{ marginBottom: 6 }}>Bonus Name</div>
                <Select
                  allowClear
                  placeholder="All"
                  style={{ width: "100%" }}
                  value={draftFilters.bonusName}
                  options={bonusNameOptions}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      bonusName: value,
                    }))
                  }
                />
              </div>
              <div style={{ flex: "1 1 200px", maxWidth: 280 }}>
                <div style={{ marginBottom: 6 }}>Bonus Preference</div>
                <Select
                  allowClear
                  placeholder="All"
                  style={{ width: "100%" }}
                  value={draftFilters.bonusPreference}
                  options={bonusPreferenceOptions}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      bonusPreference: value,
                    }))
                  }
                />
              </div>
              <div style={{ flex: "1 1 200px", maxWidth: 280 }}>
                <div style={{ marginBottom: 6 }}>Bonus Status</div>
                <Select
                  allowClear
                  placeholder="All"
                  style={{ width: "100%" }}
                  value={draftFilters.bonusStatus}
                  options={bonusStatusOptions}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      bonusStatus: value,
                    }))
                  }
                />
              </div>
              <div style={{ marginLeft: "auto" }}>
                <Space>
                  <Button onClick={resetFilters}>Reset</Button>
                  <Button type="primary" onClick={applyFilters}>
                    Apply Filters
                  </Button>
                </Space>
              </div>
            </div>
            {loading ? (
              <div className="spin-center">
                <Spin size="large" />
              </div>
            ) : (
              <Table
                dataSource={tableData}
                columns={columns}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                scroll={{ x: 3400 }}
              />
            )}
          </TabPane>

          <TabPane tab="Edit" key="edit">
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Form layout="vertical">
                <Form.Item label="Select learner">
                  <Select
                    showSearch
                    allowClear
                    placeholder="Search by name or email"
                    optionFilterProp="label"
                    value={
                      editingLearner ? String(getId(editingLearner)) : undefined
                    }
                    options={learnerOptions}
                    onChange={(id) => {
                      if (!id) {
                        setEditingLearner(null);
                        form.resetFields();
                        return;
                      }
                      onSelectLearner(id);
                    }}
                  />
                </Form.Item>
              </Form>

              {editingLearner ? (
                <>
                  <Descriptions
                    bordered
                    size="small"
                    column={{ xs: 1, sm: 2, md: 3 }}
                    title="Learner details"
                  >
                    <Descriptions.Item label="Name">
                      {editingLearner?.name ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      {editingLearner?.email ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Phone">
                      {editingLearner?.registeredPhone ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="City">
                      {editingLearner?.city ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Profession">
                      {editingLearner?.profession ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Preferred Batch">
                      {editingLearner?.preferredBatch ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="MS365 Preference">
                      {normalizePreference(editingLearner?.ms365Preference) ||
                        "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="LinkedIn Premium Preference">
                      {normalizePreference(
                        editingLearner?.linkedinPremiumPreference
                      ) || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Claude Premium Preference">
                      {normalizePreference(
                        editingLearner?.claudePremiumPreference
                      ) || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gemini Premium Preference">
                      {normalizePreference(
                        editingLearner?.geminiPremiumPreference
                      ) || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Course Status">
                      {getCourseStatusLabel(editingLearner) || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Status">
                      {editingLearner?.paymentStatus ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="AI Course Count">
                      {editingLearner?.aiCourseSubscriptionCount ??
                        getCourseSubscriptions(editingLearner).length}
                    </Descriptions.Item>
                    <Descriptions.Item label="Primary Objective" span={3}>
                      {editingLearner?.primaryObjective ?? "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="AI Course Subscriptions" span={3}>
                      {getCourseSubscriptions(editingLearner).length
                        ? getCourseSubscriptions(editingLearner)
                            .map(
                              (course) =>
                                course?.courseName || course?.courseId
                            )
                            .filter(Boolean)
                            .join(", ")
                        : "-"}
                    </Descriptions.Item>
                  </Descriptions>

                  <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Space
                      size={16}
                      style={{ display: "flex", flexWrap: "wrap" }}
                      align="start"
                    >
                      <Form.Item
                        label="MS365 Status"
                        name="ms365Status"
                        style={{ minWidth: 220 }}
                        rules={[
                          { required: true, message: "Please select MS365 status" },
                        ]}
                      >
                        <Select options={STATUS_OPTIONS} />
                      </Form.Item>
                      <Form.Item
                        label="LinkedIn Premium Status"
                        name="linkedinPremiumStatus"
                        style={{ minWidth: 220 }}
                        rules={[
                          {
                            required: true,
                            message: "Please select LinkedIn Premium status",
                          },
                        ]}
                      >
                        <Select options={STATUS_OPTIONS} />
                      </Form.Item>
                      <Form.Item
                        label="Claude Premium Status"
                        name="claudePremiumStatus"
                        style={{ minWidth: 220 }}
                        rules={[
                          {
                            required: true,
                            message: "Please select Claude Premium status",
                          },
                        ]}
                      >
                        <Select options={STATUS_OPTIONS} />
                      </Form.Item>
                      <Form.Item
                        label="Gemini Premium Status"
                        name="geminiPremiumStatus"
                        style={{ minWidth: 220 }}
                        rules={[
                          {
                            required: true,
                            message: "Please select Gemini Premium status",
                          },
                        ]}
                      >
                        <Select options={STATUS_OPTIONS} />
                      </Form.Item>
                    </Space>
                    <Button type="primary" htmlType="submit" loading={submitting}>
                      {submitting ? "Saving..." : "Update Status"}
                    </Button>
                  </Form>
                </>
              ) : (
                <p>Select a learner from the list or the dropdown to edit statuses.</p>
              )}
            </Space>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default AiOnboardedLearners;
